import { FC, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button, FieldLabel, styled } from "@monolith-forensics/monolith-ui";
import { FileIcon, UploadIcon, XIcon } from "lucide-react";

// Controlled multi-file field for the validation form. Not mfui's
// FileInputField: its onDrop replaces (rather than appends to) the selection
// and its remove Pill updates only internal state without calling onChange,
// so a parent form would submit files the user had removed. Styling follows
// _components/DataImport/steps/FileUploadStep.tsx.

export interface ValidationDocumentsFieldProps {
  files: File[];
  onChange: (files: File[]) => void;
  label?: string;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  colSpan?: number;
}

// Same file dropped twice (or picked again from the dialog) is one entry.
const fileKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;

const Container = styled.div<{ $colSpan: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: span ${({ $colSpan }) => $colSpan};
`;

const DropZone = styled.div<{
  $active: boolean;
  $error: boolean;
  $disabled: boolean;
}>`
  border: 2px dashed
    ${({ theme, $active, $error }) =>
      $active
        ? theme.palette.primary.main
        : $error
        ? theme.palette.error.main
        : theme.palette.divider};
  border-radius: 6px;
  padding: 20px 16px;
  text-align: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  outline: none;
  color: ${({ theme, $active }) =>
    $active ? theme.palette.primary.main : theme.palette.text.secondary};
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? undefined : theme.palette.primary.main};
    color: ${({ theme, $disabled }) =>
      $disabled ? undefined : theme.palette.primary.main};
  }
`;

const DropZoneTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const DropZoneHint = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileRow = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 6px;
  font-size: 12px;

  .file-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ValidationDocumentsField: FC<ValidationDocumentsFieldProps> = ({
  files,
  onChange,
  label = "Validation Documentation",
  description,
  required = false,
  error,
  disabled = false,
  colSpan = 1,
}) => {
  const handleDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length === 0) return;
      const seen = new Set(files.map(fileKey));
      const added = accepted.filter((file) => {
        const key = fileKey(file);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (added.length > 0) onChange([...files, ...added]);
    },
    [files, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: true,
    disabled,
  });

  const handleRemove = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <Container $colSpan={colSpan}>
      <FieldLabel
        size="sm"
        description={description}
        asterisk={required}
        error={error}
      >
        {label}
      </FieldLabel>
      <DropZone
        {...getRootProps({
          role: "button",
          "aria-label": label,
          "aria-invalid": !!error,
        })}
        $active={isDragActive}
        $error={!!error}
        $disabled={disabled}
      >
        <input {...getInputProps()} />
        <UploadIcon size={22} strokeWidth={1.5} />
        <DropZoneTitle>
          {isDragActive ? "Drop to add" : "Drop files or click to select"}
        </DropZoneTitle>
        <DropZoneHint>
          Reports, checklists, screenshots — any file type
        </DropZoneHint>
      </DropZone>
      {files.length > 0 && (
        <FileList aria-label="Selected validation documents">
          {files.map((file, index) => (
            <FileRow key={fileKey(file)}>
              <FileIcon size={14} />
              <span className="file-name" title={file.name}>
                {file.name}
              </span>
              <Button
                size="xxs"
                variant="subtle"
                color="error"
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                leftSection={<XIcon size={12} />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </FileRow>
          ))}
        </FileList>
      )}
    </Container>
  );
};

export default ValidationDocumentsField;
